/**
 * @fileoverview ConnectStripeButton component
 * @module stripe-connect/components/ConnectStripeButton
 *
 * Button to initiate Stripe Connect OAuth flow.
 * Redirects user to Stripe to authorize their account.
 *
 * Uses refs for callback stability to prevent OAuth effect re-runs.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ConnectStripeButtonProps } from '../types';
import { getStripeConnectService } from '../services/StripeConnectService';

/**
 * Stripe logo SVG component
 */
const StripeLogo: React.FC<{ color?: string }> = ({ color = '#635BFF' }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.976 12.312c0-.935.78-1.295 2.058-1.295 1.841 0 4.167.555 6.008 1.55V6.832c-2.013-.805-4.006-1.12-6.008-1.12-4.913 0-8.18 2.564-8.18 6.844 0 6.672 9.184 5.606 9.184 8.484 0 1.107-.97 1.467-2.321 1.467-2.006 0-4.58-.825-6.608-1.934v5.79c2.249.972 4.526 1.38 6.608 1.38 5.031 0 8.49-2.494 8.49-6.824-.018-7.202-9.231-5.919-9.231-8.627z"
      fill={color}
    />
  </svg>
);

/**
 * ConnectStripeButton - Button to start Stripe Connect OAuth flow
 *
 * @example
 * ```tsx
 * // Connect test account
 * <ConnectStripeButton
 *   projectId="proj_123"
 *   mode="test"
 *   onConnected={() => refetchProject()}
 *   onError={(err) => showError(err)}
 * />
 *
 * // Connect live account
 * <ConnectStripeButton
 *   projectId="proj_123"
 *   mode="live"
 *   onConnected={() => refetchProject()}
 * />
 * ```
 */
export const ConnectStripeButton: React.FC<ConnectStripeButtonProps> = ({
  projectId,
  mode,
  onConnected,
  onError,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  'data-testid': testId,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const service = getStripeConnectService();

  // Use refs for callbacks to prevent effect re-runs when callbacks change
  // This is the "callback ref" pattern for stable callback references
  const onConnectedRef = useRef(onConnected);
  const onErrorRef = useRef(onError);
  const processedRef = useRef(false); // Guard against double processing

  // Keep refs in sync with latest props
  useEffect(() => {
    onConnectedRef.current = onConnected;
    onErrorRef.current = onError;
  }, [onConnected, onError]);

  const handleClick = async () => {
    if (loading || disabled) return;

    setLoading(true);
    console.log(`[ConnectStripeButton] handleClick: projectId=${projectId}, mode=${mode}`);

    try {
      // Pass mode to get correct OAuth client_id (test or live)
      console.log(`[ConnectStripeButton] Calling getOAuthUrl with mode=${mode}`);
      const oauthUrl = await service.getOAuthUrl(projectId, mode);
      console.log(`[ConnectStripeButton] OAuth URL received:`, oauthUrl);
      // Store projectId and mode for OAuth callback processing
      sessionStorage.setItem('stripe_oauth_in_progress', projectId);
      sessionStorage.setItem('stripe_oauth_mode', mode);
      window.location.href = oauthUrl;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect';
      onErrorRef.current?.(errorMessage);
      setLoading(false);
    }
  };

  // Check if we're returning from OAuth - runs ONCE on mount only
  useEffect(() => {
    // Guard against double processing (React StrictMode, etc.)
    if (processedRef.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const stripeConnected = urlParams.get('stripe');
    const stripeError = urlParams.get('error');
    const callbackMode = urlParams.get('mode');
    const oauthProjectId = sessionStorage.getItem('stripe_oauth_in_progress');
    const oauthMode = sessionStorage.getItem('stripe_oauth_mode');

    // Only process if this is OUR OAuth callback (matching projectId AND mode)
    if (oauthProjectId !== projectId) return;
    if (oauthMode !== mode && callbackMode !== mode) return;

    if (stripeConnected === 'connected') {
      processedRef.current = true;
      sessionStorage.removeItem('stripe_oauth_in_progress');
      sessionStorage.removeItem('stripe_oauth_mode');
      // Clean up URL first to prevent re-processing
      window.history.replaceState({}, '', window.location.pathname);
      // Use ref to call latest callback
      onConnectedRef.current?.();
    } else if (stripeError) {
      processedRef.current = true;
      sessionStorage.removeItem('stripe_oauth_in_progress');
      sessionStorage.removeItem('stripe_oauth_mode');
      // Clean up URL first
      window.history.replaceState({}, '', window.location.pathname);
      // Use ref to call latest callback
      onErrorRef.current?.(decodeURIComponent(stripeError));
    }
  }, [projectId, mode]); // Depend on projectId and mode

  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={handleClick}
      data-testid={testId}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <StripeLogo
            color={variant === 'contained' ? '#ffffff' : theme.palette.primary.main}
          />
        )
      }
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        ...(variant === 'contained' && {
          backgroundColor: '#635BFF',
          '&:hover': {
            backgroundColor: '#4B47CC',
          },
        }),
      }}
    >
      {loading ? 'Connecting...' : `Connect ${mode === 'test' ? 'Test' : 'Live'} Account`}
    </Button>
  );
};

export default ConnectStripeButton;
